import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, Mail, Sparkles, X, Wand2, CheckCircle, Smile, Image, Code, Send, MessageCircle, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Heading1, Heading2, Quote, Type, Palette, Upload, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import useConfirm from '@/hooks/use-confirm';
import { emailTemplates, templateCategories } from '@/data/emailTemplates';

// Tiptap imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TiptapUnderline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import TiptapImage from '@tiptap/extension-image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common emojis for email marketing
const EMOJI_LIST = [
  '😊', '👋', '🎉', '✨', '🚀', '💡', '📧', '📢', '🎯', '⭐',
  '💪', '🔥', '❤️', '👍', '🎁', '📱', '💼', '🏆', '✅', '📝',
  '🌟', '💰', '🎓', '✈️', '🌍', '⚡', '🔔', '💻', '📊', '🎈',
  '🙌', '👏', '🤝', '💎', '🏅', '📈', '🎊', '🌈', '☀️', '⏰'
];

// Available custom variables
const CUSTOM_VARIABLES = [
  { key: '{{name}}', label: 'İsim', description: 'Alıcının ismi' },
  { key: '{{email}}', label: 'Email', description: 'Alıcının email adresi' },
  { key: '{{whatsapp}}', label: 'WhatsApp', description: 'WhatsApp numarası' },
  { key: '{{company}}', label: 'Şirket', description: 'Şirket adı' },
  { key: '{{course_name}}', label: 'Kurs Adı', description: 'Eğitim kurs adı' },
  { key: '{{start_date}}', label: 'Başlangıç Tarihi', description: 'Eğitim başlangıç tarihi' },
  { key: '{{instructor}}', label: 'Eğitmen', description: 'Eğitmen adı' },
  { key: '{{price}}', label: 'Fiyat', description: 'Kurs fiyatı' },
  { key: '{{website_url}}', label: 'Website URL', description: 'Web sitesi linki' },
  { key: '{{register_link}}', label: 'Kayıt Linki', description: 'Kayıt formu linki' },
  { key: '{{sender_name}}', label: 'Gönderen Adı', description: 'Email gönderen kişi' }
];

// Color palette for text
const COLOR_PALETTE = [
  '#000000', '#374151', '#6B7280', '#9CA3AF',
  '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#3B82F6', '#6366F1', '#8B5CF6', '#EC4899',
];

// Rich Text Editor Toolbar Component
const EditorToolbar = ({ editor, onEmojiClick, onVariableClick, onImageUpload, onWhatsAppClick, isUploading }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fileInputRef = useRef(null);

  if (!editor) return null;

  const addLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const setColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setShowColorPicker(false);
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="bg-gray-50 border-2 border-gray-200 rounded-t-xl p-2 flex flex-wrap gap-1 items-center shadow-sm">
      {/* Text Formatting */}
      <div className="flex gap-0.5 items-center bg-white rounded-lg p-1 border">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bold') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="h-8 w-8 p-0"
          title="Kalın"
        >
          <Bold size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('italic') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="h-8 w-8 p-0"
          title="İtalik"
        >
          <Italic size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('underline') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="h-8 w-8 p-0"
          title="Altı Çizili"
        >
          <Underline size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('strike') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className="h-8 w-8 p-0"
          title="Üstü Çizili"
        >
          <Type size={16} className="line-through" />
        </Button>
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Headings */}
      <div className="flex gap-0.5 items-center bg-white rounded-lg p-1 border">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className="h-8 w-8 p-0"
          title="Başlık 1"
        >
          <Heading1 size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className="h-8 w-8 p-0"
          title="Başlık 2"
        >
          <Heading2 size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('blockquote') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className="h-8 w-8 p-0"
          title="Alıntı"
        >
          <Quote size={16} />
        </Button>
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Lists */}
      <div className="flex gap-0.5 items-center bg-white rounded-lg p-1 border">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className="h-8 w-8 p-0"
          title="Madde İşaretli Liste"
        >
          <List size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className="h-8 w-8 p-0"
          title="Numaralı Liste"
        >
          <ListOrdered size={16} />
        </Button>
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Alignment */}
      <div className="flex gap-0.5 items-center bg-white rounded-lg p-1 border">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className="h-8 w-8 p-0"
          title="Sola Hizala"
        >
          <AlignLeft size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className="h-8 w-8 p-0"
          title="Ortala"
        >
          <AlignCenter size={16} />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className="h-8 w-8 p-0"
          title="Sağa Hizala"
        >
          <AlignRight size={16} />
        </Button>
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Color */}
      <div className="relative">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="h-8 px-2 bg-white border"
          title="Yazı Rengi"
        >
          <Palette size={16} className="mr-1" />
          <span className="text-xs">Renk</span>
        </Button>
        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-2 z-50 grid grid-cols-4 gap-1">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setColor(color)}
                className="w-6 h-6 rounded border hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        )}
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Link */}
      <div className="relative">
        {showLinkInput ? (
          <div className="flex gap-1 items-center">
            <Input
              placeholder="https://..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-40 h-8 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && addLink()}
            />
            <Button type="button" size="sm" onClick={addLink} className="h-8 px-2">
              Ekle
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowLinkInput(false)} className="h-8 w-8 p-0">
              <X size={14} />
            </Button>
          </div>
        ) : (
          <div className="flex gap-0.5">
            <Button
              type="button"
              size="sm"
              variant={editor.isActive('link') ? 'default' : 'ghost'}
              onClick={() => setShowLinkInput(true)}
              className="h-8 px-2 bg-white border"
              title="Link Ekle"
            >
              <LinkIcon size={16} className="mr-1" />
              <span className="text-xs">Link</span>
            </Button>
            {editor.isActive('link') && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={removeLink}
                className="h-8 w-8 p-0 text-red-500"
                title="Linki Kaldır"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="h-6 w-px bg-gray-300 mx-1" />

      {/* Special Buttons */}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onEmojiClick}
        className="h-8 px-2 bg-white border"
      >
        <Smile size={16} className="mr-1" />
        <span className="text-xs">Emoji</span>
      </Button>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onVariableClick}
        className="h-8 px-2 bg-white border"
      >
        <Code size={16} className="mr-1" />
        <span className="text-xs">Değişken</span>
      </Button>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
      />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={handleImageSelect}
        disabled={isUploading}
        className="h-8 px-2 bg-white border"
      >
        {isUploading ? (
          <Loader2 size={16} className="mr-1 animate-spin" />
        ) : (
          <Upload size={16} className="mr-1" />
        )}
        <span className="text-xs">{isUploading ? 'Yükleniyor...' : 'Resim Yükle'}</span>
      </Button>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onWhatsAppClick}
        className="h-8 px-2 bg-green-50 border border-green-200 text-green-700 hover:bg-green-100"
      >
        <MessageCircle size={16} className="mr-1" />
        <span className="text-xs">WhatsApp</span>
      </Button>
    </div>
  );
};

const Templates = () => {
  const { confirm, confirmDialog } = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestEmailDialogOpen, setIsTestEmailDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // HTML kaynak modu: ham HTML yapıştırma/yükleme (Tiptap karmaşık email HTML'ini bozar).
  const [htmlMode, setHtmlMode] = useState(false);
  const htmlFileInputRef = useRef(null);
  // onUpdate closure'ı için güncel htmlMode (stale closure'ı önler).
  const htmlModeRef = useRef(false);
  useEffect(() => { htmlModeRef.current = htmlMode; }, [htmlMode]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('Merhaba!');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Editor refs
  const subjectInputRef = useRef(null);
  const contentTextareaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_content: '',
    variables: []
  });
  const [editingId, setEditingId] = useState(null);

  // Tiptap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TiptapUnderline,
      TextStyle,
      Color,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #3b82f6; text-decoration: underline;',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TiptapImage.configure({
        inline: true,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0;',
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose max-w-none focus:outline-none min-h-[400px] p-6',
        style: 'font-family: Arial, sans-serif;',
      },
    },
    onUpdate: ({ editor }) => {
      // HTML kaynak modundayken textarea kaynaktır; editör içeriği formData'yı ezmesin.
      if (htmlModeRef.current) return;
      setFormData(prev => ({ ...prev, html_content: editor.getHTML() }));
    },
  });

  // formData -> editör senkronu. HTML modunda çalışmaz (ham HTML'i Tiptap bozmasın);
  // görsel moda dönüldüğünde htmlMode değiştiği için tetiklenir ve içerik yüklenir.
  useEffect(() => {
    if (htmlMode) return;
    if (editor && formData.html_content && editor.getHTML() !== formData.html_content) {
      editor.commands.setContent(formData.html_content);
    }
  }, [editor, formData.html_content, htmlMode]);

  // Insert emoji into editor
  const insertEmojiToEditor = useCallback((emoji) => {
    if (activeField === 'subject' && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newValue = formData.subject.substring(0, start) + emoji + formData.subject.substring(end);
      setFormData({ ...formData, subject: newValue });
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else if (editor) {
      editor.chain().focus().insertContent(emoji).run();
    }
    setShowEmojiPicker(false);
  }, [activeField, formData, editor]);

  // Insert variable into editor
  const insertVariableToEditor = useCallback((variable) => {
    if (activeField === 'subject' && subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newValue = formData.subject.substring(0, start) + variable + formData.subject.substring(end);
      setFormData({ ...formData, subject: newValue });
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else if (editor) {
      editor.chain().focus().insertContent(variable).run();
    }
    setShowVariables(false);
  }, [activeField, formData, editor]);

  // Upload image to server and insert into editor
  const uploadImageToServer = useCallback(async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Geçersiz dosya türü. Sadece JPEG, PNG, GIF, WEBP kabul edilir.');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan büyük olamaz.');
      return;
    }
    
    setIsUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success && response.data.url) {
        // Ensure full URL for email compatibility
        let imageUrl = response.data.url;
        if (imageUrl.startsWith('/')) {
          // Convert relative URL to absolute
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
        }
        
        // Insert image into editor
        if (editor) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
        }
        toast.success('Resim yüklendi ve eklendi! 📸');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.detail || 'Resim yüklenemedi');
    } finally {
      setIsUploadingImage(false);
    }
  }, [editor]);

  // Add WhatsApp button to editor
  const addWhatsAppButtonToEditor = useCallback(() => {
    if (!whatsappNumber.trim()) {
      toast.error('Lütfen bir WhatsApp numarası girin');
      return;
    }
    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '');
    const whatsappHTML = `<div style="text-align: center; margin: 30px 0;"><a href="https://wa.me/${cleanNumber}?text=${encodeURIComponent(whatsappMessage)}" style="display: inline-block; background: linear-gradient(135deg, #25D366 0%, #128C7E 100%); color: white; text-decoration: none; padding: 16px 32px; border-radius: 50px; font-weight: bold; font-size: 16px;">💬 WhatsApp ile İletişime Geç</a></div>`;
    
    if (editor) {
      editor.chain().focus().insertContent(whatsappHTML).run();
    }
    
    setShowWhatsAppDialog(false);
    setWhatsappNumber('');
    setWhatsappMessage('Merhaba!');
    toast.success('WhatsApp butonu eklendi! 💬');
  }, [whatsappNumber, whatsappMessage, editor]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Şablonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/templates/${editingId}`, formData);
        toast.success('Şablon güncellendi');
      } else {
        await axios.post(`${API}/templates`, formData);
        toast.success('Şablon oluşturuldu');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Şablon silinsin mi?',
      description: 'Bu şablon kalıcı olarak silinecek. Gönderilmiş kampanyalar etkilenmez.',
      confirmLabel: 'Sil',
      destructive: true,
      onConfirm: () => doDeleteTemplate(id),
    });
  };

  const doDeleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success('Şablon silindi');
      fetchTemplates();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleEdit = (template) => {
    setFormData({
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      variables: template.variables || []
    });
    setEditingId(template.id);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', subject: '', html_content: '', variables: [] });
    setEditingId(null);
    setShowEmojiPicker(false);
    setShowVariables(false);
    if (editor) {
      editor.commands.clearContent();
    }
  };

  const handleUseTemplate = (template) => {
    setFormData({
      name: template.name,
      subject: template.subject,
      html_content: template.html,
      variables: []
    });
    setIsGalleryOpen(false);
    setIsDialogOpen(true);
  };

  // Send test email
  const handleSendTestEmail = async (template) => {
    setTestingTemplate(template);
    setIsTestEmailDialogOpen(true);
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Lütfen bir email adresi girin');
      return;
    }

    setSendingTest(true);
    try {
      await axios.post(`${API}/send-test-email`, {
        template_id: testingTemplate.id,
        test_email: testEmail
      });
      toast.success(`Test email ${testEmail} adresine gönderildi! 📧`);
      setIsTestEmailDialogOpen(false);
      setTestEmail('');
      setTestingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test email gönderilemedi');
    } finally {
      setSendingTest(false);
    }
  };

  const filteredTemplateGallery = selectedCategory === 'all' 
    ? emailTemplates 
    : emailTemplates.filter(t => t.category === selectedCategory);

  const [aiPreviewData, setAiPreviewData] = useState(null);

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Lütfen ne tür bir email şablonu istediğinizi yazın');
      return;
    }

    setAiGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-template`, {
        prompt: aiPrompt,
        tone: aiTone
      });

      // Show preview first
      setAiPreviewData(response.data);
      toast.success('AI şablon oluşturuldu! Önizlemeyi kontrol edin.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI şablon oluşturulamadı');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUseAITemplate = () => {
    setFormData({
      name: aiPreviewData.name,
      subject: aiPreviewData.subject,
      html_content: aiPreviewData.html_content,
      variables: []
    });
    
    setIsAIDialogOpen(false);
    setIsDialogOpen(true);
    setAiPrompt('');
    setAiPreviewData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] tracking-tight">Şablonlar</h1>
          <p className="text-sm text-[#64748b] mt-1">Email şablonlarınızı oluşturun ve yönetin</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setIsAIDialogOpen(true)}
            data-testid="open-ai-generator"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white flex items-center gap-2"
          >
            <Wand2 size={20} />
            AI ile Oluştur
          </Button>
          <Button
            onClick={() => setIsGalleryOpen(true)}
            data-testid="open-template-gallery"
            variant="outline"
            className="flex items-center gap-2"
          >
            <Sparkles size={20} />
            Hazır Şablonlar
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-template-btn"
                className="bg-[#6366f1] hover:bg-[#5558e3] text-white shadow-sm rounded-lg px-6 py-3 font-medium flex items-center gap-2"
              >
                <Plus size={20} />
                Şablon Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{editingId ? 'Şablonu Düzenle' : 'Yeni Şablon Oluştur'}</DialogTitle>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={htmlMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        // HTML moduna geçerken önizleme kapansın; editördeki içerik korunur.
                        setHtmlMode(!htmlMode);
                        setShowPreview(false);
                      }}
                      title="Ham HTML kodunu düzenle / yapıştır"
                    >
                      <Code size={16} className="mr-2" />
                      {htmlMode ? 'Görsel Editör' : 'HTML'}
                    </Button>
                    <Button
                      type="button"
                      variant={showPreview ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      <Eye size={16} className="mr-2" />
                      {showPreview ? 'Düzenleme' : 'Önizleme'}
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4 flex-1 min-h-0 flex flex-col">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Şablon Adı</Label>
                    <Input
                      id="name"
                      data-testid="template-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Email Konusu</Label>
                    <div className="relative">
                      <Input
                        ref={subjectInputRef}
                        id="subject"
                        data-testid="template-subject-input"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        onFocus={() => setActiveField('subject')}
                        required
                        className="mt-1 pr-20"
                        placeholder="{{name}} için özel teklif"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setActiveField('subject');
                            setShowEmojiPicker(!showEmojiPicker);
                            setShowVariables(false);
                          }}
                          className="h-8 w-8 p-0"
                          title="Emoji ekle"
                        >
                          <Smile size={16} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setActiveField('subject');
                            setShowVariables(!showVariables);
                            setShowEmojiPicker(false);
                          }}
                          className="h-8 w-8 p-0"
                          title="Değişken ekle"
                        >
                          <Code size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* min-h-0 + overflow-y-auto: editor/onizleme icerigi dialogdan
                    uzun oldugunda kirpilmadan kaysin (eskiden overflow-hidden idi). */}
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  {showPreview ? (
                    <div className="h-full">
                      <Label>Önizleme</Label>
                      <div className="mt-2 border-2 border-[#e2e8f0] rounded-lg p-4 sm:p-6 bg-[#f8fafc] min-h-[300px] overflow-y-auto">
                        <div 
                          className="bg-white shadow-lg mx-auto"
                          style={{ maxWidth: '600px' }}
                          dangerouslySetInnerHTML={{ 
                            __html: formData.html_content
                              .replace(/\{\{name\}\}/g, 'Ahmet Yılmaz')
                              .replace(/\{\{email\}\}/g, 'ahmet@example.com')
                              .replace(/\{\{whatsapp\}\}/g, '+90 555 123 4567')
                              .replace(/\{\{company\}\}/g, 'Şirketiniz')
                              .replace(/\{\{course_name\}\}/g, 'Kabin Memuru Eğitimi')
                              .replace(/\{\{start_date\}\}/g, '15 Ocak 2025')
                              .replace(/\{\{instructor\}\}/g, 'Ayşe Demir')
                              .replace(/\{\{price\}\}/g, '4.999 TL')
                              .replace(/\{\{website_url\}\}/g, '#')
                              .replace(/\{\{register_link\}\}/g, '#')
                              .replace(/\{\{sender_name\}\}/g, 'Eğitim Koordinatörü')
                              .replace(/\{\{year\}\}/g, new Date().getFullYear())
                          }} 
                        />
                      </div>
                    </div>
                  ) : htmlMode ? (
                    <div className="relative h-full">
                      {/* Ham HTML kaynak modu */}
                      <div className="flex items-center justify-between mb-2">
                        <Label>HTML Kaynak Kodu</Label>
                        <div className="flex gap-2">
                          <input
                            ref={htmlFileInputRef}
                            type="file"
                            accept=".html,.htm,text/html"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error('HTML dosyası 2MB\'dan büyük olamaz');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                const html = String(ev.target?.result || '');
                                setFormData((prev) => ({ ...prev, html_content: html }));
                                toast.success(`${file.name} yüklendi`);
                              };
                              reader.readAsText(file);
                              e.target.value = '';
                            }}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => htmlFileInputRef.current?.click()}
                          >
                            <Upload size={15} className="mr-2" />
                            HTML Dosyası Yükle
                          </Button>
                        </div>
                      </div>
                      <textarea
                        value={formData.html_content}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, html_content: e.target.value }));
                        }}
                        spellCheck={false}
                        placeholder="<html>...</html> — hazır email HTML'inizi buraya yapıştırın veya yukarıdan dosya yükleyin."
                        className="w-full h-[450px] border-2 border-gray-200 rounded-xl p-4 font-mono text-sm text-gray-800 bg-[#0f172a08] focus:outline-none focus:ring-2 focus:ring-[#6366f1] resize-none"
                      />
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <strong>⚠️ HTML modu:</strong> Tablolu, inline stilli hazır email
                          şablonlarını buraya yapıştırın — kod olduğu gibi kaydedilir.
                          Görsel editöre dönerseniz karmaşık yapılar sadeleşebilir.
                          Değişkenler için <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code> kullanın.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-full">
                      {/* Tiptap Rich Text Editor */}
                      <EditorToolbar
                        editor={editor}
                        onEmojiClick={() => {
                          setActiveField('content');
                          setShowEmojiPicker(!showEmojiPicker);
                          setShowVariables(false);
                        }}
                        onVariableClick={() => {
                          setActiveField('content');
                          setShowVariables(!showVariables);
                          setShowEmojiPicker(false);
                        }}
                        onImageUpload={uploadImageToServer}
                        onWhatsAppClick={() => setShowWhatsAppDialog(true)}
                        isUploading={isUploadingImage}
                      />
                      
                      {/* Rich Text Editor Content */}
                      <div className="border-2 border-t-0 border-gray-200 rounded-b-xl overflow-hidden bg-white min-h-[450px]">
                        <EditorContent 
                          editor={editor} 
                          className="min-h-[450px] [&_.ProseMirror]:min-h-[450px] [&_.ProseMirror]:p-6 [&_.ProseMirror]:outline-none [&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:mb-4 [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mb-3 [&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:mb-2 [&_.ProseMirror_p]:mb-3 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:ml-6 [&_.ProseMirror_ul]:mb-3 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:ml-6 [&_.ProseMirror_ol]:mb-3 [&_.ProseMirror_li]:mb-1 [&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-gray-300 [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:italic [&_.ProseMirror_blockquote]:text-gray-600 [&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline"
                        />
                      </div>
                      
                      {/* Helper Text */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <strong>💡 İpucu:</strong> Metni seçip formatlama butonlarını kullanın. Word gibi yazın, otomatik olarak HTML'e dönüştürülür.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <div className="bg-white border-2 border-[#6366f1] rounded-lg p-4 shadow-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-[#0f172a]">Emoji Seç</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowEmojiPicker(false)}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-10 gap-2 max-h-48 overflow-y-auto">
                      {EMOJI_LIST.map((emoji, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => insertEmojiToEditor(emoji)}
                          className="text-2xl hover:bg-[#f1f5f9] rounded p-2 transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variable Picker */}
                {showVariables && (
                  <div className="bg-white border-2 border-[#6366f1] rounded-lg p-4 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-[#0f172a]">Değişken Seç - Kişiselleştirme</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowVariables(false)}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <p className="text-xs text-[#64748b] mb-3">
                      💡 Değişkenler email gönderilirken alıcıya özel verilerle değiştirilir
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                      {CUSTOM_VARIABLES.map((variable, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => insertVariableToEditor(variable.key)}
                          className="text-left p-3 hover:bg-[#f1f5f9] rounded-lg border border-[#e2e8f0] transition-colors hover:border-[#6366f1]"
                        >
                          <div className="font-mono text-sm text-[#6366f1] font-semibold bg-[#ede9fe] px-2 py-1 rounded inline-block">
                            {variable.key}
                          </div>
                          <div className="text-xs text-[#64748b] mt-2">{variable.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <Button type="submit" data-testid="save-template-btn" className="flex-1 bg-[#6366f1] hover:bg-[#5558e3] text-white">
                    {editingId ? 'Güncelle' : 'Oluştur'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setIsDialogOpen(false); resetForm(); }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-[#e2e8f0]">
          <Mail size={64} className="mx-auto text-[#cbd5e1] mb-4" />
          <h3 className="text-lg font-bold text-[#0f172a] mb-2">Henüz şablon yok</h3>
          <p className="text-[#64748b] mb-6">Hazır şablonlardan seçin veya sıfırdan oluşturun</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => setIsGalleryOpen(true)}
              variant="outline"
            >
              <Sparkles size={20} className="mr-2" />
              Hazır Şablonlar
            </Button>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#6366f1] hover:bg-[#5558e3] text-white"
            >
              <Plus size={20} className="mr-2" />
              Şablon Oluştur
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              data-testid={`template-card-${template.id}`}
              className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden card-hover"
            >
              <div className="bg-[#f8fafc] h-40 flex items-center justify-center border-b border-[#e2e8f0]">
                <Mail size={48} className="text-[#cbd5e1]" />
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-[#0f172a] text-lg mb-1 truncate">{template.name}</h3>
                <p className="text-sm text-[#64748b] mb-4 truncate">{template.subject}</p>
                
                {/* Butonlar kart genisligine gore dizilir — ekran breakpoint'ine
                    gore DEGIL. Grid 3-4 kolona cikinca kart daraliyor ve eski
                    yatay dizilim "Duzenle"/"Sil" butonlarini kartin disina
                    tasiriyordu (overflow-hidden yuzunden tiklanamaz oluyordu). */}
                <div className="space-y-2">
                  <button
                    data-testid={`test-template-${template.id}`}
                    onClick={() => handleSendTestEmail(template)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#10b981] hover:bg-[#059669] text-white rounded-lg transition-colors text-sm font-medium"
                    title="Test Email Gönder"
                  >
                    <Send size={15} className="shrink-0" />
                    <span className="truncate">Test Gönder</span>
                  </button>
                  <div className="flex gap-2">
                    <button
                      data-testid={`preview-template-${template.id}`}
                      onClick={() => setPreviewTemplate(template)}
                      className="flex-1 min-w-0 flex items-center justify-center gap-1 px-1.5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors text-xs font-medium"
                      title="Önizle"
                    >
                      <Eye size={15} className="shrink-0" />
                      <span className="truncate">Önizle</span>
                    </button>
                    <button
                      data-testid={`edit-template-${template.id}`}
                      onClick={() => handleEdit(template)}
                      className="flex-1 min-w-0 flex items-center justify-center gap-1 px-1.5 py-2 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors text-xs font-medium"
                      title="Düzenle"
                    >
                      <Edit size={15} className="shrink-0" />
                      <span className="truncate">Düzenle</span>
                    </button>
                    <button
                      data-testid={`delete-template-${template.id}`}
                      onClick={() => handleDelete(template.id)}
                      className="flex-1 min-w-0 flex items-center justify-center gap-1 px-1.5 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors text-xs font-medium"
                      title="Sil"
                    >
                      <Trash2 size={15} className="shrink-0" />
                      <span className="truncate">Sil</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Gallery Modal */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl">Hazır Email Şablonları</DialogTitle>
                <p className="text-sm text-[#64748b] mt-1">Profesyonel şablonlardan birini seçin ve özelleştirin</p>
              </div>
            </div>
          </DialogHeader>
          
          {/* Categories */}
          <div className="flex gap-2 py-4 border-b border-[#e2e8f0] overflow-x-auto">
            {templateCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-[#6366f1] text-white'
                    : 'bg-[#f8fafc] text-[#64748b] hover:bg-[#f1f5f9]'
                }`}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplateGallery.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-xl border-2 border-[#e2e8f0] overflow-hidden hover:border-[#6366f1] transition-all card-hover cursor-pointer"
                  onClick={() => handleUseTemplate(template)}
                >
                  <div className="h-48 overflow-hidden bg-[#f8fafc]">
                    <img 
                      src={template.thumbnail} 
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-[#0f172a]">{template.name}</h3>
                      <span className="text-xs px-2 py-1 bg-[#6366f1]/10 text-[#6366f1] rounded-full">{template.category}</span>
                    </div>
                    <p className="text-sm text-[#64748b]">{template.description}</p>
                    <Button 
                      className="w-full mt-4 bg-[#6366f1] hover:bg-[#5558e3] text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUseTemplate(template);
                      }}
                    >
                      Bu Şablonu Kullan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generator Dialog */}
      <Dialog open={isAIDialogOpen} onOpenChange={(open) => {
        setIsAIDialogOpen(open);
        if (!open) {
          setAiPreviewData(null);
          setAiPrompt('');
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="text-purple-600" size={24} />
              AI ile Email Şablonu Oluştur
            </DialogTitle>
            <p className="text-sm text-[#64748b] mt-2">
              Ne tür bir email göndermek istediğinizi yazın, AI sizin için profesyonel bir şablon oluşturacak.
            </p>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto">
            {aiPreviewData ? (
              <div className="h-full flex flex-col">
                <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={20} className="text-green-600" />
                    <h4 className="font-bold text-green-900">Şablon Oluşturuldu!</h4>
                  </div>
                  <p className="text-sm text-green-700">
                    <strong>İsim:</strong> {aiPreviewData.name}<br />
                    <strong>Konu:</strong> {aiPreviewData.subject}
                  </p>
                </div>
                
                <div className="flex-1 overflow-y-auto border-2 border-[#e2e8f0] rounded-lg p-6 bg-[#f8fafc]">
                  <div 
                    className="bg-white shadow-lg mx-auto"
                    style={{ maxWidth: '600px' }}
                    dangerouslySetInnerHTML={{ 
                      __html: aiPreviewData.html_content
                        .replace(/\{\{name\}\}/g, 'John Doe')
                        .replace(/\{\{email\}\}/g, 'john@example.com')
                        .replace(/\{\{company\}\}/g, 'Şirketiniz')
                        .replace(/\{\{year\}\}/g, new Date().getFullYear())
                        .replace(/\{\{website\}\}/g, '#')
                    }} 
                  />
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={handleUseAITemplate}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Bu Şablonu Kullan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setAiPreviewData(null)}
                    className="flex-1"
                  >
                    Yeni Şablon Oluştur
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="ai-prompt">Ne tür bir email istiyorsunuz?</Label>
              <Textarea
                id="ai-prompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Örnek: Yeni müşteriler için hoş geldin emaili oluştur. Modern ve profesyonel olsun. Şirket logosu, hoş geldin mesajı ve ilk adımlar bölümü olsun."
                className="mt-2 min-h-[120px]"
                disabled={aiGenerating}
              />
            </div>

            <div>
              <Label htmlFor="ai-tone">Ton</Label>
              <select
                id="ai-tone"
                value={aiTone}
                onChange={(e) => setAiTone(e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-[#e2e8f0] rounded-lg"
                disabled={aiGenerating}
              >
                <option value="professional">Profesyonel</option>
                <option value="friendly">Samimi</option>
                <option value="formal">Resmi</option>
                <option value="casual">Gündelik</option>
                <option value="enthusiastic">Coşkulu</option>
              </select>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-medium text-[#0f172a] mb-2 flex items-center gap-2">
                <Sparkles size={16} className="text-purple-600" />
                İpuçları:
              </h4>
              <ul className="text-sm text-[#64748b] space-y-1 list-disc list-inside">
                <li>Ne tür bir email olduğunu belirtin (hoş geldin, promosyon, haber bülteni)</li>
                <li>Hangi bölümleri istediğinizi yazın</li>
                <li>Renk veya stil tercihinizi ekleyebilirsiniz</li>
                <li>Değişkenler otomatik eklenecek ({'{{name}}'}, {'{{email}}'})</li>
              </ul>
            </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleAIGenerate}
                    disabled={aiGenerating || !aiPrompt.trim()}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  >
                    {aiGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Oluşturuluyor...
                      </>
                    ) : (
                      <>
                        <Wand2 size={18} className="mr-2" />
                        AI ile Oluştur
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAIDialogOpen(false);
                      setAiPrompt('');
                    }}
                    disabled={aiGenerating}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={isTestEmailDialogOpen} onOpenChange={setIsTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Email Gönder 📧</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>{testingTemplate?.name}</strong> şablonu test edilecek
              </p>
              <p className="text-xs text-blue-700 mt-2">
                Tüm değişkenler örnek verilerle doldurulacak
              </p>
            </div>
            <div>
              <Label htmlFor="test-email">Test Email Adresi</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="mt-2"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="flex-1 bg-[#10b981] hover:bg-[#059669] text-white"
              >
                {sendingTest ? 'Gönderiliyor...' : 'Test Email Gönder'}
              </Button>
              <Button
                onClick={() => {
                  setIsTestEmailDialogOpen(false);
                  setTestEmail('');
                  setTestingTemplate(null);
                }}
                variant="outline"
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with Mobile/Desktop View */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="mb-4 p-3 sm:p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <p className="text-xs sm:text-sm font-medium text-[#64748b] mb-1">Konu:</p>
              <p className="text-sm sm:text-base text-[#0f172a] font-medium">{previewTemplate?.subject}</p>
            </div>
            
            {/* Desktop and Mobile Preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Desktop Preview - Hidden on mobile */}
              <div className="hidden lg:block">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <p className="text-sm font-semibold text-[#0f172a]">💻 Desktop Görünüm</p>
                </div>
                <div className="border-2 border-[#e2e8f0] rounded-lg p-4 sm:p-6 bg-white overflow-auto" style={{maxHeight: '600px'}}>
                  <div dangerouslySetInnerHTML={{ __html: previewTemplate?.html_content }} />
                </div>
              </div>
              
              {/* Mobile Preview */}
              <div className="flex justify-center lg:justify-start">
                <div className="w-full max-w-[375px]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <p className="text-sm font-semibold text-[#0f172a]">📱 Mobil Görünüm</p>
                  </div>
                  <div className="border-4 border-[#1e293b] rounded-2xl sm:rounded-3xl p-2 bg-[#1e293b] shadow-2xl w-full">
                    <div className="bg-white rounded-xl sm:rounded-2xl overflow-auto" style={{height: '500px', maxHeight: '650px'}}>
                      <div className="p-3 sm:p-4 text-xs sm:text-sm" dangerouslySetInnerHTML={{ __html: previewTemplate?.html_content }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Button Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="text-green-600" />
              WhatsApp Butonu Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="whatsapp-number">
                WhatsApp Numarası
                <span className="text-xs text-gray-500 ml-2">(Ülke kodu ile)</span>
              </Label>
              <Input
                id="whatsapp-number"
                placeholder="905551234567"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500">
                Örnek: 905551234567 (Türkiye için 90 ile başlar)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="whatsapp-message">
                Varsayılan Mesaj
                <span className="text-xs text-gray-500 ml-2">(Opsiyonel)</span>
              </Label>
              <Textarea
                id="whatsapp-message"
                placeholder="Merhaba! Bilgi almak istiyorum."
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Bu mesaj, WhatsApp&apos;ta otomatik olarak yazılacak
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800 font-medium mb-1">
                📱 Önizleme
              </p>
              <p className="text-xs text-green-700">
                Buton tıklandığında: <br />
                <code className="bg-white px-1 rounded">
                  wa.me/{whatsappNumber || 'NUMARA'}
                </code>
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowWhatsAppDialog(false)}
                className="flex-1"
              >
                İptal
              </Button>
              <Button
                type="button"
                onClick={addWhatsAppButtonToEditor}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <MessageCircle size={16} className="mr-2" />
                Butonu Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
};

export default Templates;
